import React, { useEffect, useState } from "react";
import axios from "axios";
import { TRACKS, ROLES } from "@/components/onboarding/questions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminHeaders = () => ({ headers: { "x-admin-token": localStorage.getItem("cw_admin_token") || "" } });
const roleLabel = (v) => ROLES.find((r) => r.v === v)?.l || "Not chosen";

export default function OnboardingAdmin() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try { const r = await axios.get(`${API}/admin/onboarding`, adminHeaders()); setItems(r.data.items || []); }
      finally { setBusy(false); }
    })();
  }, []);

  const open = async (id) => { const r = await axios.get(`${API}/admin/onboarding/${id}`, adminHeaders()); setDetail(r.data); };

  return (
    <div data-testid="admin-onboarding">
      <div className="cw-eyebrow">Foundation Discovery</div>
      <h1 className="cw-display-l" style={{ marginTop: 8 }}>Discovery sessions.</h1>
      <p className="cw-body" style={{ marginTop: 8, color: "rgba(22,30,27,0.72)" }}>
        Every Foundation Discovery session started at /onboarding. {items.length} total, {items.filter((i) => i.status === "submitted").length} submitted.
      </p>
      <div className="cw-card" style={{ marginTop: 24, padding: 0 }}>
        <table className="cw-table">
          <thead><tr><th>Organisation</th><th>Respondent</th><th>Track</th><th>Seat</th><th>Status</th><th>Answered</th><th>Updated</th><th /></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`onb-row-${it.id}`}>
                <td style={{ fontWeight: 500 }}>{it.organisation}</td>
                <td className="cw-tabular" style={{ fontSize: 12.5 }}>{it.lead_name ? `${it.lead_name} · ` : ""}{it.lead_email}</td>
                <td>{it.track === "tech" ? "Technical" : "CEO"}</td>
                <td>{roleLabel(it.respondent_role)}</td>
                <td><span className={`pill ${it.status === "submitted" ? "pill-seafoam" : "pill-marigold"}`}>{it.status}</span></td>
                <td className="cw-tabular">{it.answered}/{it.total}{it.skipped ? ` · ${it.skipped} skipped` : ""}</td>
                <td className="cw-tabular" style={{ fontSize: 12.5 }}>{(it.updated_at || "").slice(0, 16).replace("T", " ")}</td>
                <td><button className="cw-btn-text" onClick={() => open(it.id)} data-testid={`onb-open-${it.id}`}>Open</button></td>
              </tr>
            ))}
            {!items.length && !busy && <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "rgba(22,30,27,0.55)" }}>No sessions yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {detail && <Detail cfg={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

const cell = (x) => (x == null || x === "" ? "—" : Array.isArray(x) ? x.map(cell).join(", ") : typeof x === "object" ? Object.entries(x).map(([k, y]) => `${k}: ${cell(y)}`).join("; ") : typeof x === "boolean" ? (x ? "Yes" : "No") : String(x));

function Value({ v }) {
  if (v == null || v === "") return <span style={{ opacity: .5 }}>—</span>;
  if (Array.isArray(v) && v.length && typeof v[0] === "object") {
    const cols = [...new Set(v.flatMap((o) => Object.keys(o)))];
    return (
      <table className="cw-table" style={{ marginTop: 6 }}>
        <thead><tr>{cols.map((c) => <th key={c}>{c.replace(/_/g, " ")}</th>)}</tr></thead>
        <tbody>{v.map((o, i) => <tr key={i}>{cols.map((c) => <td key={c}>{cell(o[c])}</td>)}</tr>)}</tbody>
      </table>
    );
  }
  if (!Array.isArray(v) && typeof v === "object") {
    return <dl style={{ margin: 0 }}>{Object.entries(v).map(([k, x]) => <div key={k} style={{ marginBottom: 6 }}><b>{k.replace(/_/g, " ")}:</b> {cell(x)}</div>)}</dl>;
  }
  return <span style={{ whiteSpace: "pre-wrap" }}>{cell(v)}</span>;
}

function VoiceRow({ cfgId, note, i }) {
  const [url, setUrl] = useState(null);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const load = async () => {
    const r = await axios.get(`${API}/admin/onboarding/${cfgId}/voice/${note.id}`, { ...adminHeaders(), responseType: "blob" });
    setUrl(URL.createObjectURL(r.data));
  };
  return (
    <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, background: "rgba(22,30,27,0.04)", display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }} data-testid={`onb-voice-${note.id}`}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div className="cw-eyebrow" style={{ fontSize: 11 }}>Voice note {i + 1} · {Math.round(note.duration || 0)}s · {(note.created_at || "").slice(0, 16).replace("T", " ")}</div>
        <div style={{ marginTop: 4, fontSize: 14, whiteSpace: "pre-wrap" }}>{note.transcript || <em style={{ opacity: .6 }}>No transcript; listen to the recording.</em>}</div>
      </div>
      {url ? <audio controls src={url} style={{ height: 36 }} /> : <button className="cw-btn-text" onClick={load} data-testid={`onb-voice-load-${note.id}`}>Load recording</button>}
    </div>
  );
}

function Detail({ cfg, onClose }) {
  return (
    <div style={{ marginTop: 32 }} data-testid="onb-detail">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <h2 className="cw-heading-m">{cfg.organisation} · {cfg.lead_email} · {roleLabel(cfg.respondent_role)}</h2>
        <button className="cw-btn-text" onClick={onClose} data-testid="onb-detail-close">Close</button>
      </div>
      {TRACKS[cfg.track || "ceo"].sections.map((s) => {
        const qs = cfg.configuration.filter((q) => q.section === s.key);
        return (
          <div className="cw-card" style={{ marginTop: 16 }} key={s.key}>
            <div className="cw-eyebrow">{s.n} · {s.title}</div>
            {qs.map((q) => (
              <div key={q.id} style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid rgba(22,30,27,0.08)" }} data-testid={`onb-q-${q.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <b>{q.label}</b>
                  <span style={{ fontSize: 12.5, color: "rgba(22,30,27,0.6)" }}>{q.config_object} → {q.capability}</span>
                </div>
                <div style={{ marginTop: 6, fontSize: 14 }}>
                  {q.routed_to ? <em>Routed to {q.routed_to}</em> : q.answered ? <Value v={q.value} /> : <em style={{ opacity: .6 }}>{q.skipped ? "Skipped for now" : "Not answered"}</em>}
                </div>
                {(q.voice_notes || []).map((n, i) => <VoiceRow key={n.id} cfgId={cfg.id} note={n} i={i} />)}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
